// =============================================================================
// CAPABILITY ASSESSMENT TYPES
// =============================================================================
//
// Prescriptieve beslislaag voor skill/capacity gaps.
// GEEN coaching, GEEN interpretatie, keuzedwingend.
//
// =============================================================================

// -----------------------------------------------------------------------------
// CANONICAL DOMAINS (vast, niet uitbreiden zonder nieuw anker)
// -----------------------------------------------------------------------------

export type CapabilityDomain =
  | 'strategy'      // Strategie & Richting
  | 'marketing'     // Marketing & Zichtbaarheid
  | 'sales'         // Sales & Conversie
  | 'product'       // Product / Dienst
  | 'operations'    // Operatie & Systemen
  | 'finance'       // Financiën
  | 'team';         // Team & Capaciteit

export const CAPABILITY_DOMAIN_LABELS: Record<CapabilityDomain, string> = {
  strategy: 'Strategie & Richting',
  marketing: 'Marketing & Zichtbaarheid',
  sales: 'Sales & Conversie',
  product: 'Product / Dienst',
  operations: 'Operatie & Systemen',
  finance: 'Financiën',
  team: 'Team & Capaciteit',
};

// -----------------------------------------------------------------------------
// DECISION TYPES
// -----------------------------------------------------------------------------

export type Responsibility =
  | 'self'      // Ikzelf
  | 'other'     // Iemand anders
  | 'unknown';  // Nog onbekend (blokkeert flow)

export const RESPONSIBILITY_LABELS: Record<Responsibility, string> = {
  self: 'Ikzelf',
  other: 'Iemand anders',
  unknown: 'Nog onbekend',
};

export type SkillLevel =
  | 'basic'      // Basis
  | 'workable'   // Werkbaar
  | 'advanced';  // Gevorderd

export const SKILL_LEVEL_LABELS: Record<SkillLevel, string> = {
  basic: 'Basis',
  workable: 'Werkbaar',
  advanced: 'Gevorderd',
};

export type Resolution =
  | 'learn'      // Ik ga dit leren
  | 'outsource'  // Ik besteed dit uit
  | 'hire';      // Ik haal iemand erbij

export const RESOLUTION_LABELS: Record<Resolution, string> = {
  learn: 'Ik ga dit leren',
  outsource: 'Ik besteed dit uit',
  hire: 'Ik haal iemand erbij',
};

// -----------------------------------------------------------------------------
// CAPABILITY DECISION (per domein)
// -----------------------------------------------------------------------------

export interface CapabilityDecision {
  domain: CapabilityDomain;
  required: boolean;                  // Is dit domein nodig voor dit doel?
  responsibility?: Responsibility;    // Wie draagt verantwoordelijkheid?
  level?: SkillLevel;                 // Niveau (alleen als responsibility = 'self')
  resolution?: Resolution;            // Oplossing (alleen als level = 'basic' | 'workable')
}

// -----------------------------------------------------------------------------
// CAPABILITY ASSESSMENT (complete flow)
// -----------------------------------------------------------------------------

export interface CapabilityAssessment {
  id: string;
  goalId: string;
  ownerId: string;
  decisions: CapabilityDecision[];
  completedAt: number;
  createdAt: number;
}

// -----------------------------------------------------------------------------
// GENERATED MILESTONE (from assessment)
// -----------------------------------------------------------------------------

export interface GeneratedMilestone {
  title: string;
  domain: CapabilityDomain;
  resolution: Resolution | 'delegate';  // 'delegate' for responsibility='other'
  globalPeriod?: string;
}

// -----------------------------------------------------------------------------
// VALIDATION RESULT
// -----------------------------------------------------------------------------

export interface ValidationResult {
  isValid: boolean;
  missingDecisions: CapabilityDomain[];  // Domeinen waar keuzes ontbreken
  blockingDomains: CapabilityDomain[];   // Domeinen met responsibility='unknown'
}

// =============================================================================
// EXTENDED ASSESSMENT TYPES (DISC, Werkstijl, Extended Skills)
// =============================================================================

// -----------------------------------------------------------------------------
// DISC PERSONALITY TEST
// -----------------------------------------------------------------------------

export type DISCColor = 'red' | 'yellow' | 'green' | 'blue';

export interface DISCAnswer {
  questionId: number;
  selectedOption: number;
  color: DISCColor;
  points: number;
}

export interface DISCScores {
  red: number;      // Dominant - Doelgericht, besluitvaardig
  yellow: number;   // Invloedrijk - Enthousiast, sociaal
  green: number;    // Stabiel - Geduldig, betrouwbaar
  blue: number;     // Consciëntieus - Analytisch, precies
}

export interface DISCProfile {
  primary: DISCColor;
  secondary: DISCColor;
  scores: DISCScores;
  description: string;
  entrepreneurialStrengths: string[];
  entrepreneurialWeaknesses: string[];
  completedAt: number;
}

export const DISC_COLOR_LABELS: Record<DISCColor, string> = {
  red: 'Rood (Dominant)',
  yellow: 'Geel (Invloedrijk)',
  green: 'Groen (Stabiel)',
  blue: 'Blauw (Consciëntieus)',
};

export const DISC_COLOR_DESCRIPTIONS: Record<DISCColor, string> = {
  red: 'Doelgericht, besluitvaardig, directe communicatie',
  yellow: 'Enthousiast, sociaal, optimistisch',
  green: 'Geduldig, betrouwbaar, teamgericht',
  blue: 'Analytisch, precies, systematisch',
};

// -----------------------------------------------------------------------------
// WERKSTIJL / WORKSTYLE TEST (Kolbe-inspired)
// -----------------------------------------------------------------------------

export type WorkstyleDimension = 
  | 'fact_finder'   // Research vs Actie (0-10)
  | 'follow_thru'   // Chaos vs Structuur (0-10)
  | 'quick_start'   // Afwachten vs Risico (0-10)
  | 'implementor';  // Abstract vs Hands-on (0-10)

export interface WorkstyleScores {
  fact_finder: number;   // 0-10: Hoeveel research doe je?
  follow_thru: number;   // 0-10: Hoe systematisch werk je?
  quick_start: number;   // 0-10: Hoe snel neem je risico's?
  implementor: number;   // 0-10: Werk je hands-on of conceptueel?
}

export interface WorkstyleProfile {
  scores: WorkstyleScores;
  primaryStrength: WorkstyleDimension;
  primaryWeakness: WorkstyleDimension;
  energyGivers: string[];
  energyDrainers: string[];
  idealTasks: string[];
  avoidTasks: string[];
  completedAt: number;
}

export const WORKSTYLE_DIMENSION_LABELS: Record<WorkstyleDimension, string> = {
  fact_finder: 'Fact Finder (Onderzoek)',
  follow_thru: 'Follow Through (Structuur)',
  quick_start: 'Quick Start (Risico)',
  implementor: 'Implementor (Hands-on)',
};

export const WORKSTYLE_DIMENSION_DESCRIPTIONS: Record<WorkstyleDimension, { low: string; high: string }> = {
  fact_finder: {
    low: 'Je duikt direct in zonder veel onderzoek',
    high: 'Je doet grondig onderzoek voordat je start'
  },
  follow_thru: {
    low: 'Je werkt graag met vrijheid en flexibiliteit',
    high: 'Je werkt graag met duidelijke structuur'
  },
  quick_start: {
    low: 'Je denkt lang na voordat je actie onderneemt',
    high: 'Je springt snel op nieuwe kansen'
  },
  implementor: {
    low: 'Je werkt liever met concepten en ideeën',
    high: 'Je werkt graag hands-on met tastbare dingen'
  }
};

// -----------------------------------------------------------------------------
// EXTENDED SKILLS ASSESSMENT
// -----------------------------------------------------------------------------

export type QuestionType = 
  | 'text'          // Vrije tekst invoer
  | 'textarea'      // Lange tekst
  | 'select'        // Dropdown keuze
  | 'multiselect'   // Meerdere opties
  | 'scale';        // 1-5 schaal

export interface AssessmentQuestion {
  id: string;
  type: QuestionType;
  question: string;
  placeholder?: string;
  options?: string[];
  scale?: string[];
  required?: boolean;
}

export interface AssessmentSection {
  title: string;
  description?: string;
  questions: AssessmentQuestion[];
}

export interface ExtendedDomainAssessment {
  domain: CapabilityDomain;
  sections: AssessmentSection[];
}

export interface ExtendedCapabilityDecision extends CapabilityDecision {
  // Bestaande velden
  domain: CapabilityDomain;
  required: boolean;
  responsibility?: Responsibility;
  level?: SkillLevel;
  resolution?: Resolution;
  
  // Nieuwe extended velden
  extendedAnswers?: Record<string, any>; // Key = question ID, Value = answer
  currentActivities?: string[];
  timeInvestment?: string;
  toolsUsed?: string;
  skillRatings?: Record<string, number>; // Skill ID → 1-5 rating
  biggestChallenge?: string;
  missingKnowledge?: string[];
  firstAction?: string;
  timeline?: string;
  budget?: string;
}

// -----------------------------------------------------------------------------
// ASSESSMENT LEVEL & COMPLETENESS
// -----------------------------------------------------------------------------

export type AssessmentLevel = 
  | 'none'      // Geen assessment
  | 'basic'     // Snelle Scan (7 domains) - 5-10 min
  | 'full'      // Volledig (DISC + Werkstijl + 7 domains) - 25-30 min
  | 'extended'; // Ultra Uitgebreid (Full + Extended Skills 100+ vragen) - 60-90 min

export interface AssessmentCompleteness {
  level: AssessmentLevel;
  hasDISC: boolean;
  hasWorkstyle: boolean;
  hasExtendedSkills: boolean;
  completenessPercentage: number; // 0-100
}

// -----------------------------------------------------------------------------
// COMPLETE ASSESSMENT (all data)
// -----------------------------------------------------------------------------

export interface CompleteAssessment extends CapabilityAssessment {
  // Bestaande velden
  id: string;
  goalId: string;
  ownerId: string;
  decisions: CapabilityDecision[];
  completedAt: number;
  createdAt: number;
  
  // Nieuwe velden
  assessmentLevel: AssessmentLevel;
  discProfile?: DISCProfile;
  workstyleProfile?: WorkstyleProfile;
  extendedDecisions?: ExtendedCapabilityDecision[];
}